import * as Dialog from "@radix-ui/react-dialog";
import { TRPCClientError } from "@trpc/client";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { ReactComponent as SubmitIcon } from "assets/img/icons/submit.svg";
import { Button } from "components/elements";
import { TextArea } from "components/elements/TextArea";
import { trpc } from "lib/trpc";
import type { CalendarFriendlyCompassEvent } from "./types";

type CompassEventFormModalProps = {
    isOpen: boolean;
    onClose: () => void;
    editEvent: CalendarFriendlyCompassEvent | null;
};

export function CompassEventFormModal({ isOpen, onClose, editEvent }: CompassEventFormModalProps) {
    const { enqueueSnackbar } = useSnackbar();

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [startDate, setStartDate] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endDate, setEndDate] = useState("");
    const [endTime, setEndTime] = useState("");
    const [errors, setErrors] = useState({
        title: "",
        startDate: "",
        startTime: "",
    });

    const utils = trpc.useContext();

    const { mutate: createEvent, isPending: isCreating } = trpc.compassEvents.create.useMutation({
        onSuccess: () => {
            utils.compassEvents.latest.invalidate();
            utils.compassEvents.allFutureEvents.invalidate();
            enqueueSnackbar("Event created successfully", { variant: "success" });
            handleClose();
        },
        onError: (error) => {
            console.error("Error creating event:", error);
            const errorMessage = extractTRPCErrorMessage(error);
            enqueueSnackbar(errorMessage, { variant: "error" });
        },
    });

    const { mutate: updateEvent, isPending: isUpdating } = trpc.compassEvents.update.useMutation({
        onSuccess: () => {
            utils.compassEvents.latest.invalidate();
            utils.compassEvents.allFutureEvents.invalidate();
            enqueueSnackbar("Event updated successfully", { variant: "success" });
            handleClose();
        },
        onError: (error) => {
            console.error("Error updating event:", error);
            const errorMessage = extractTRPCErrorMessage(error);
            enqueueSnackbar(errorMessage, { variant: "error" });
        },
    });

    const extractTRPCErrorMessage = (error: unknown): string => {
        if (!(error instanceof TRPCClientError)) {
            return "An unexpected error occurred";
        }

        const zodError = error.data?.zodError;

        // Try to extract from flat structure
        if (zodError?.flat?.properties?.compassEvent?.end?.errors?.[0]) {
            return zodError.flat.properties.compassEvent.end.errors[0];
        }

        // Try to extract from complex structure
        if (zodError?.complex?.compassEvent?.end?._errors?.[0]) {
            return zodError.complex.compassEvent.end._errors[0];
        }

        // Check for other field errors in flat structure
        if (zodError?.flat?.properties?.compassEvent) {
            const compassEventProps = zodError.flat.properties.compassEvent;
            for (const field in compassEventProps.properties || {}) {
                const fieldErrors = compassEventProps.properties[field]?.errors;
                if (fieldErrors && fieldErrors.length > 0) {
                    return fieldErrors[0];
                }
            }
        }

        // Fallback to the error message
        return error.message || "Validation error occurred";
    };

    useEffect(() => {
        if (editEvent) {
            setTitle(editEvent.title);
            setDescription(editEvent.description || "");
            setStartDate(dayjs(editEvent.start).format("YYYY-MM-DD"));
            setStartTime(dayjs(editEvent.start).format("HH:mm"));
            if (editEvent.end) {
                setEndDate(dayjs(editEvent.end).format("YYYY-MM-DD"));
                setEndTime(dayjs(editEvent.end).format("HH:mm"));
            } else {
                setEndDate("");
                setEndTime("");
            }
        } else {
            resetForm();
        }
    }, [editEvent, isOpen]);

    const resetForm = () => {
        setTitle("");
        setDescription("");
        setStartDate("");
        setStartTime("");
        setEndDate("");
        setEndTime("");
        setErrors({
            title: "",
            startDate: "",
            startTime: "",
        });
    };

    const validateForm = () => {
        const newErrors = {
            title: "",
            startDate: "",
            startTime: "",
        };

        if (!title.trim()) {
            newErrors.title = "Title is required";
        }

        if (!startDate) {
            newErrors.startDate = "Start date is required";
        }

        if (!startTime) {
            newErrors.startTime = "Start time is required";
        }

        setErrors(newErrors);
        return !newErrors.title && !newErrors.startDate && !newErrors.startTime;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        // Combine date and time for start
        const startDateTime = new Date(`${startDate}T${startTime}`);

        // Combine date and time for end (if provided)
        let endDateTime: Date | null = null;
        if (endDate && endTime) {
            endDateTime = new Date(`${endDate}T${endTime}`);
        }

        const compassEvent = {
            title,
            description: description || null,
            start: startDateTime,
            end: endDateTime,
        };

        if (editEvent) {
            updateEvent({
                id: editEvent.id,
                compassEvent,
            });
        } else {
            createEvent({
                compassEvent,
            });
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog.Root open={isOpen} onOpenChange={handleClose}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
                <Dialog.Content className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="max-h-[95vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-none">
                        <div className="flex items-center justify-between">
                            <Dialog.Title className="text-left align-middle font-sans text-[24px] font-bold leading-[100%] tracking-[0] text-[#424242]">
                                {editEvent ? "Edit Event" : "Create New Event"}
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button>
                                    <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                                </button>
                            </Dialog.Close>
                        </div>
                        <hr className="-mx-[20px] my-4 border-[#c8c8c8]" />

                        <h3 className="mb-4 font-sans text-[20px] font-semibold leading-[100%] tracking-[0] text-[#424242]">
                            Event Information
                        </h3>

                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            className="space-y-4"
                        >
                            <div className="flex items-center gap-3">
                                <label className="w-[30%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Title <span className="text-red-400">*</span>
                                </label>
                                <div className="flex w-full flex-col">
                                    <input
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                        placeholder="Event Title"
                                    />
                                    {errors.title && (
                                        <p className="mt-1 text-xs text-red-400">{errors.title}</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <label className="w-[30%] pt-2 font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Description
                                </label>
                                <TextArea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Event Description"
                                    rows={4}
                                    className="w-full"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[30%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Start Date <span className="text-red-400">*</span>
                                </label>
                                <div className="flex w-full flex-col">
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                    />
                                    {errors.startDate && (
                                        <p className="mt-1 text-xs text-red-400">
                                            {errors.startDate}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[30%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    Start Time <span className="text-red-400">*</span>
                                </label>
                                <div className="flex w-full flex-col">
                                    <input
                                        type="time"
                                        value={startTime}
                                        onChange={(e) => setStartTime(e.target.value)}
                                        className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                    />
                                    {errors.startTime && (
                                        <p className="mt-1 text-xs text-red-400">
                                            {errors.startTime}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[30%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="w-[30%] font-sans text-[16px] font-normal leading-normal text-[#424242]">
                                    End Time
                                </label>
                                <input
                                    type="time"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    className="focus:ring-brand-atlantic w-full rounded-lg border border-[#c8c8c8] bg-[#eeeeee] px-3 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2"
                                />
                            </div>

                            <div className="mt-6 flex justify-end">
                                <Button
                                    color="blue"
                                    variant="primary"
                                    isLoading={isCreating || isUpdating}
                                    type="submit"
                                    disabled={!title || !startDate || !startTime}
                                >
                                    Submit <SubmitIcon className="ml-2" />
                                </Button>
                            </div>
                        </form>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
